import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm,
  Tag,
  Card,
  Typography,
  Switch,
  Dropdown,
  Upload,
  Image,
  Progress
} from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, MoreOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import apiService from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface News {
  _id: string;
  title: string;
  content: string;
  summary: string;
  category: string;
  author: string;
  tags: string[];
  isPublished: boolean;
  coverImage?: string;
  publishDate?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

interface UploadResponse {
  success: boolean;
  data?: {
    filename: string;
    originalName: string;
    url: string;
    size: number;
    mimetype: string;
  };
  message?: string;
}

const NewsManagement: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [html, setHtml] = useState<string>('');

  const categories = ['学术会议', '合作交流', '实验室活动', '获奖消息', '招生/招聘信息', '其他'];

  useEffect(() => {
    fetchNews();
  }, []);

  // 组件卸载时清理编辑器
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  // 确保form在组件挂载后正确初始化
  useEffect(() => {
    if (form && modalVisible) {
      form.resetFields();
    }
  }, [form, modalVisible]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<any>('/admin/news') as any;
      // 后端直接返回 {news, total, page, pages} 结构
      setNews(response.news || []);
    } catch (error) {
      console.error('获取新闻列表失败:', error);
      message.error('获取新闻列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingNews(null);
    form.resetFields();
    setCoverImageUrl(''); // 重置封面图片状态
    setHtml(''); // 重置富文本内容
    setModalVisible(true);
  };

  const handleEdit = (record: News) => {
    setEditingNews(record);
    // 延迟设置表单值，确保富文本编辑器能正确加载内容
    setTimeout(() => {
      form.setFieldsValue({
        ...record,
        isPublished: record.isPublished
      });
      // 设置封面图片URL状态
      if (record.coverImage) {
        setCoverImageUrl(record.coverImage);
      }
      // 设置富文本内容
      setHtml(record.content || '');
    }, 100);
    setModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(() => {
      handleSubmit(form.getFieldsValue());
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/admin/news/${id}`);
      message.success('删除成功');
      fetchNews();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await apiService.put(`/admin/news/${id}`, {
        status: newStatus ? 'published' : 'draft'
      });
      message.success(newStatus ? '发布成功' : '取消发布成功');
      fetchNews();
    } catch (error) {
      message.error('状态切换失败');
    }
  };

  const handleBatchDelete = async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.delete(`/admin/news/${id}`)));
      message.success(`成功删除 ${ids.length} 条新闻`);
      fetchNews();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await apiService.upload<UploadResponse>('/upload/image', formData, {
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      if (response.success) {
        const imageUrl = (response.data as any)?.url;
        message.success('封面图片上传成功');
        // 更新表单中的封面图片字段
        form.setFieldsValue({ coverImage: imageUrl });
        setCoverImageUrl(imageUrl);
        return false; // 阻止默认上传行为
      } else {
        message.error(response.message || '图片上传失败');
        return false;
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      message.error('图片上传失败');
      return false;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // 富文本编辑器配置
  const toolbarConfig: Partial<IToolbarConfig> = {
    excludeKeys: [
      'group-video', // 排除视频
      'fullScreen', // 排除全屏
    ],
  };

  const editorConfig: Partial<IEditorConfig> = {
    placeholder: '请输入新闻内容...',
    onChange: (editor: IDomEditor) => {
      const html = editor.getHtml();
      setHtml(html);
      form.setFieldsValue({ content: html });
    },
    MENU_CONF: {
      uploadImage: {
        server: 'https://ynuzhangwenhua.cn/api/upload/image',
        fieldName: 'image',
        maxFileSize: 50 * 1024 * 1024, // 50MB
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        customInsert: (res: any, insertFn: any) => {
          if (res.success) {
            const imageUrl = `https://ynuzhangwenhua.cn${res.data.url}`;
            insertFn(imageUrl, res.data.originalName, imageUrl);
          } else {
            message.error(res.message || '图片上传失败');
          }
        },
      },
    },
  };

  const uploadProps = {
    name: 'image',
    showUploadList: false,
    beforeUpload: handleImageUpload,
    accept: 'image/*',
  };

  const handleSubmit = async (values: any) => {
    try {
      // 验证富文本编辑器内容
      if (!html.trim()) {
        message.error('请输入新闻内容');
        return;
      }
      
      const submitData = {
        ...values,
        content: html, // 使用富文本编辑器的内容
        status: values.isPublished ? 'published' : 'draft'
      };
      
      console.log('提交的数据:', submitData);
      
      if (editingNews) {
        await apiService.put(`/admin/news/${editingNews._id}`, submitData);
        message.success('更新成功');
      } else {
        await apiService.post('/admin/news', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchNews();
    } catch (error: any) {
      console.error('提交失败:', error);
      const errorMessage = error.response?.data?.message || error.message || '操作失败';
      message.error(`操作失败: ${errorMessage}`);
    }
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 300,
    },
    {
      title: '类别',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <Tag color="blue">{category}</Tag>
      ),
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 120,
      render: (isPublished: boolean, record: News) => (
        <Space>
          <Tag color={isPublished ? 'green' : 'orange'}>
            {isPublished ? '已发布' : '草稿'}
          </Tag>
          <Switch
            checked={isPublished}
            onChange={() => handleToggleStatus(record._id, isPublished)}
            size="small"
          />
        </Space>
      ),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => (
        <Space>
          {tags.map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: News) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => navigate(`/news/${record._id}`)}
          >
            查看
          </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'toggle',
                  label: record.isPublished ? '取消发布' : '发布',
                  onClick: () => handleToggleStatus(record._id, record.isPublished)
                },
                {
                  key: 'delete',
                  label: '删除',
                  danger: true,
                  onClick: () => handleDelete(record._id)
                }
              ]
            }}
          >
            <Button type="link" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={3}>新闻管理</Title>
          <Space>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 条新闻吗？`}
                onConfirm={() => {
                  handleBatchDelete(selectedRowKeys);
                  setSelectedRowKeys([]);
                }}
                okText="确定"
                cancelText="取消"
              >
                <Button danger>批量删除</Button>
              </Popconfirm>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加新闻
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={news}
          rowKey="_id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedRowKeys) => setSelectedRowKeys(selectedRowKeys as string[]),
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingNews ? '编辑新闻' : '添加新闻'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        width={800}
        okText={editingNews ? '更新' : '创建'}
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入新闻标题" />
          </Form.Item>

          <Form.Item
            name="category"
            label="新闻类别"
            rules={[{ required: true, message: '请选择新闻类别' }]}
          >
            <Select placeholder="请选择新闻类别">
              {categories.map(category => (
                <Option key={category} value={category}>{category}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="summary"
            label="摘要"
            rules={[{ required: true, message: '请输入摘要' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="请输入新闻摘要" 
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <div style={{ border: '1px solid #ccc', zIndex: 100 }}>
              <Toolbar
                editor={editor}
                defaultConfig={toolbarConfig}
                mode="default"
                style={{ borderBottom: '1px solid #ccc' }}
              />
              <Editor
                defaultConfig={editorConfig}
                value={html}
                onCreated={setEditor}
                onChange={(editor) => {
                  const html = editor.getHtml();
                  setHtml(html);
                  form.setFieldsValue({ content: html });
                }}
                mode="default"
                style={{ height: '400px', overflowY: 'hidden' }}
              />
            </div>
          </Form.Item>

          <Form.Item
            name="author"
            label="作者"
            rules={[{ required: true, message: '请输入作者' }]}
          >
            <Input placeholder="请输入作者" />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select
              mode="tags"
              placeholder="请输入标签"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="isPublished"
            label="发布状态"
            rules={[{ required: true, message: '请选择发布状态' }]}
          >
            <Select placeholder="请选择发布状态">
              <Option value={true}>发布</Option>
              <Option value={false}>草稿</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="coverImage"
            label="封面图片"
          >
            <div>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? '上传中...' : '上传封面图片'}
                </Button>
              </Upload>
              
              {uploading && (
                <div style={{ marginTop: '8px' }}>
                  <Progress 
                    percent={uploadProgress} 
                    status="active"
                    size="small"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    正在上传图片...
                  </div>
                </div>
              )}
              
              {(coverImageUrl || form.getFieldValue('coverImage')) && !uploading && (
                <div style={{ marginTop: '8px' }}>
                  <Image
                    width={200}
                    src={coverImageUrl ? `https://ynuzhangwenhua.cn${coverImageUrl}` : `https://ynuzhangwenhua.cn${form.getFieldValue('coverImage')}`}
                    alt="封面图片"
                    style={{ borderRadius: '4px' }}
                  />
                  <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>
                    ✅ 封面图片已上传
                  </div>
                </div>
              )}
            </div>
          </Form.Item>

        </Form>
      </Modal>
    </div>
  );
};

export default NewsManagement; 